import { Font, View } from '@react-pdf/renderer';
import { IBusinessToolType } from 'constants/businessPlan';
import { InfoTable } from 'PDF/components/InfoTable';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { IInfoTableRow, prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import { getSummaryChapterNames, SummaryKeys, SummarySectionAvailability } from 'PDF/types/pdfSections';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { SectionCoverPagePdf } from '../SectionCoverPage';
import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);

export interface ISummaryPdfProps {
  magazineItems: ITextItem[][];
  data: {
    financialInfo: IInfoTableRow[];
    financingNeedsInfo: IInfoTableRow[];
  };
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean
}

export const SummaryPdf = (
  props: ISummaryPdfProps & {
    availability: SummarySectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    magazineItems,
    data: { financialInfo, financingNeedsInfo },
    businessPlanType,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage
  } = props;
  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.SECTION_SUMMARY, isExamplePage);
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const SUMMARY_CHAPTER_NAMES = getSummaryChapterNames(isExamplePage);
  const subChapterNames = (Object.keys(availability) as SummaryKeys[])
    .filter((key) => !!availability[key])
    .map((key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${SUMMARY_CHAPTER_NAMES[key]}`);
  const isSectionEmpty = Object.values(availability).every((value) => !value);
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.SECTION_SUMMARY)}
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 1, variant: 'after' }}
          subChapterNames={subChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.goalsAndObjectives && (
            <>
              <SubChapterHeading
                id="goalsAndObjectives"
                heading={getSubHeading(SUMMARY_CHAPTER_NAMES.goalsAndObjectives)}
              />
              <PdfContentText items={magazineItems[0]} withoutBottomMargin={!availability.companyDescription} />
            </>
          )}
          {availability.companyDescription && (
            <>
              <SubChapterHeading
                id="companyDescription"
                heading={getSubHeading(SUMMARY_CHAPTER_NAMES.companyDescription)}
              />
              <PdfContentText items={magazineItems[1]} />
            </>
          )}
          {availability.productsOrServices && (
            <>
              <SubChapterHeading
                id="productsOrServices"
                heading={getSubHeading(SUMMARY_CHAPTER_NAMES.productsOrServices)}
              />
              <PdfContentText items={magazineItems[2]} />
            </>
          )}
          {availability.marketAndCustomers && (
            <>
              <SubChapterHeading
                id="marketAndCustomers"
                heading={getSubHeading(SUMMARY_CHAPTER_NAMES.marketAndCustomers)}
              />
              <PdfContentText items={magazineItems[3]} />
            </>
          )}
          {availability.organisationAndTeam && (
            <>
              <SubChapterHeading
                id="organisationAndTeam"
                heading={getSubHeading(SUMMARY_CHAPTER_NAMES.organisationAndTeam)}
              />
              <PdfContentText items={magazineItems[4]} />
            </>
          )}
          {availability.financialInformation && (
            <View wrap={false}>
              <SubChapterHeading
                id="financialInformation"
                heading={getSubHeading(SUMMARY_CHAPTER_NAMES.financialInformation)}
              />
              <InfoTable rows={[financialInfo]} titleTextWeight={300} />
            </View>
          )}
          {availability.financingNeeds && (
            <View wrap={false}>
              <SubChapterHeading id="financingNeeds" heading={getSubHeading(SUMMARY_CHAPTER_NAMES.financingNeeds)} />
              <InfoTable rows={[financingNeedsInfo]} titleTextWeight={300} />
            </View>
          )}
          {availability.riskManagementStrategies && (
            <>
              <SubChapterHeading
                id="riskManagementStrategies"
                heading={getSubHeading(SUMMARY_CHAPTER_NAMES.riskManagementStrategies)}
              />
              <PdfContentText items={magazineItems[5]} />
            </>
          )}
        </PDFPage>
      )}
    </>
  );
};
